import * as React from 'react'
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Alert — inline status message (Alert / AlertIndicator / AlertContent /
 * AlertTitle / AlertDescription), from the TailGrids alert model + the Figma
 * alert specs.
 *
 *   status  default | success | warning | error | info
 *   size    sm | md   (sm is the default; md is the larger Figma size)
 *
 * Colors come straight from the Figma "Alerts/*" tokens — note warning is
 * AMBER (matching the Badge) and info is SKY: each status is a -50 surface, -200 border,
 * -800 title, -700 description, and a -600/-700 accent for the icon + close.
 *
 * `onClose` (additive to the TailGrids API, matching Figma) renders a trailing
 * × button. Root is role="alert" so it's announced by assistive tech.
 *
 * Actions: render a neutral outline <Button appearance="outline"> + a <Link>
 * inside AlertContent — NOT status-colored fills (a green fill on a yellow
 * alert clashes). This keeps the action treatment consistent across statuses.
 */

type AlertStatus = 'default' | 'success' | 'warning' | 'error' | 'info'
type AlertSize = 'md' | 'sm'

const containerColor: Record<AlertStatus, string> = {
  default: 'bg-fbui-gray-50 border-fbui-gray-200',
  success: 'bg-fbui-green-50 border-fbui-green-200',
  warning: 'bg-fbui-amber-50 border-fbui-amber-200',
  error: 'bg-fbui-red-50 border-fbui-red-200',
  info: 'bg-fbui-sky-50 border-fbui-sky-200',
}

/**
 * Icon + close accent (Figma "Close Icon" per status). Warning uses amber-700
 * (not -600): amber-600 on amber-50 sits near the 3:1 non-text contrast floor,
 * amber-700 clears it comfortably.
 */
const accentColor: Record<AlertStatus, string> = {
  default: 'text-fbui-gray-600',
  success: 'text-fbui-green-600',
  warning: 'text-fbui-amber-700',
  error: 'text-fbui-red-700',
  info: 'text-fbui-sky-600',
}

const titleColor: Record<AlertStatus, string> = {
  default: 'text-fbui-gray-800',
  success: 'text-fbui-green-800',
  warning: 'text-fbui-amber-800',
  error: 'text-fbui-red-800',
  info: 'text-fbui-sky-800',
}

const descColor: Record<AlertStatus, string> = {
  default: 'text-fbui-gray-700',
  success: 'text-fbui-green-700',
  warning: 'text-fbui-amber-700',
  error: 'text-fbui-red-700',
  info: 'text-fbui-sky-700',
}

const defaultIcon: Record<AlertStatus, Icon> = {
  default: Info,
  success: CheckCircle,
  warning: Warning,
  error: WarningCircle,
  info: Info,
}

interface AlertContextValue {
  status: AlertStatus
  size: AlertSize
  inline: boolean
}
const AlertContext = React.createContext<AlertContextValue | null>(null)
const useAlert = (part: string) => {
  const ctx = React.useContext(AlertContext)
  if (!ctx) throw new Error(`<${part}> must be used within <Alert>`)
  return ctx
}

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: AlertStatus
  size?: AlertSize
  /** Single-line layout — icon, message, and close sit in one centered row. */
  inline?: boolean
  /** When provided, renders a trailing × button that calls this on click. */
  onClose?: () => void
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      status = 'default',
      size = 'sm',
      inline = false,
      onClose,
      children,
      ...props
    },
    ref
  ) => (
    <AlertContext.Provider value={{ status, size, inline }}>
      <div
        ref={ref}
        role="alert"
        data-slot="alert"
        data-status={status}
        className={cn(
          'flex border',
          inline ? 'items-center' : 'items-start',
          size === 'sm' ? 'gap-2.5 rounded-md p-3' : 'gap-3 rounded-lg p-4',
          containerColor[status],
          className
        )}
        {...props}
      >
        {children}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className={cn(
              '-m-1 ml-auto inline-flex shrink-0 items-center justify-center rounded-full p-1 opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-fbui-ring-default',
              accentColor[status]
            )}
          >
            <X
              weight="bold"
              className={size === 'sm' ? 'size-4' : 'size-5'}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </AlertContext.Provider>
  )
)
Alert.displayName = 'Alert'

export interface AlertIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {}

export const AlertIndicator = React.forwardRef<
  HTMLSpanElement,
  AlertIndicatorProps
>(({ className, children, ...props }, ref) => {
  const { status, size, inline } = useAlert('AlertIndicator')
  const StatusIcon = defaultIcon[status]
  return (
    <span
      ref={ref}
      data-slot="alert-indicator"
      data-status={status}
      aria-hidden="true"
      role="presentation"
      className={cn(
        'shrink-0 [&>svg]:block',
        !inline && 'mt-px',
        size === 'sm' ? '[&>svg]:size-4' : '[&>svg]:size-5',
        accentColor[status],
        className
      )}
      {...props}
    >
      {children ?? <StatusIcon weight="fill" />}
    </span>
  )
})
AlertIndicator.displayName = 'AlertIndicator'

export type AlertContentProps = React.HTMLAttributes<HTMLDivElement>

export const AlertContent = React.forwardRef<HTMLDivElement, AlertContentProps>(
  ({ className, ...props }, ref) => {
    const { size, inline } = useAlert('AlertContent')
    return (
      <div
        ref={ref}
        data-slot="alert-content"
        className={cn(
          'flex min-w-0 flex-1',
          inline
            ? 'flex-row flex-wrap items-center gap-x-2 gap-y-0.5'
            : 'flex-col',
          !inline && (size === 'sm' ? 'gap-0.5' : 'gap-1'),
          className
        )}
        {...props}
      />
    )
  }
)
AlertContent.displayName = 'AlertContent'

export interface AlertTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level (1–6). Defaults to 4. */
  level?: 1 | 2 | 3 | 4 | 5 | 6
}

export const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, level = 4, ...props }, ref) => {
    const { status, size } = useAlert('AlertTitle')
    const Heading = `h${level}` as const
    return (
      <Heading
        ref={ref}
        data-slot="alert-title"
        data-status={status}
        className={cn(
          'font-semibold tracking-[-0.2px]',
          size === 'sm' ? 'text-sm' : 'text-base',
          titleColor[status],
          className
        )}
        {...props}
      />
    )
  }
)
AlertTitle.displayName = 'AlertTitle'

export type AlertDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  AlertDescriptionProps
>(({ className, ...props }, ref) => {
  const { status, size } = useAlert('AlertDescription')
  return (
    <p
      ref={ref}
      data-slot="alert-description"
      data-status={status}
      className={cn(
        'tracking-[-0.2px]',
        size === 'sm' ? 'text-xs' : 'text-sm',
        descColor[status],
        className
      )}
      {...props}
    />
  )
})
AlertDescription.displayName = 'AlertDescription'
